// src/components/ProdMgrs/ProdMgrsMonthlyNetSalesTable.jsx
// =============================================================================
// BLOCK PMGR.MNS.TABLE.0 — ProdMgrs Monthly Net Sales Table
// Fixes:
//  - Use local component ProdMgrsMonthlyNetSalesRow (no shared cross-dir import).
//  - Use "rows" state (NOT undefined "monthlyNetSales").
//  - Add missing TableCell import.
//  - Guard rendering so undefined rows can’t crash the app.
// =============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
import MonthlyNetSalesHeader from '../../MonthlyNetSalesHeader.jsx';
import ProdMgrsMonthlyNetSalesRow from './ProdMgrsMonthlyNetSalesRow.jsx';

function ProdMgrsMonthlyNetSalesTable({ selectedRep, selectedYear }) {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // ===========================================================================
  // BLOCK PMGR.MNS.TABLE.1 — Fetch
  // ===========================================================================
  React.useEffect(() => {
    if (!selectedRep || !selectedYear) {
      setRows([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    axios
      .get('/api/prod-mgrs/monthly-net-sales', {
        params: { rep: selectedRep, year: selectedYear },
      })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setError(e);
        setRows([]);
      })
      .finally(() => setIsLoading(false));
  }, [selectedRep, selectedYear]);

  // ===========================================================================
  // BLOCK PMGR.MNS.TABLE.2 — Render
  // ===========================================================================
  return (
    <Paper elevation={3} sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Monthly Net Sales
      </Typography>

      {!selectedRep ? <Alert severity="info">Select a rep to view monthly net sales.</Alert> : null}

      {error ? <Alert severity="error">Failed to load monthly net sales.</Alert> : null}

      {isLoading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer>
          <Table
            size="small"
            stickyHeader
            sx={{
              // Category column stays left
              '& .MuiTableCell-head:first-of-type': { textAlign: 'left' },
              '& .MuiTableCell-body:first-of-type': { textAlign: 'left' },

              // All other header/body cells right aligned
              '& .MuiTableCell-head:not(:first-of-type)': { textAlign: 'right' },
              '& .MuiTableCell-body:not(:first-of-type)': { textAlign: 'right' },

              // (optional) make sure numbers line up nicely
              '& .MuiTableCell-head, & .MuiTableCell-body': { whiteSpace: 'nowrap' },
            }}
          >
            <MonthlyNetSalesHeader />

            <TableBody>
              {rows.length ? (
                rows.map((r, idx) => (
                  <ProdMgrsMonthlyNetSalesRow key={r?.category ?? `mns-${idx}`} netSale={r} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="20" style={{ textAlign: 'center', padding: '1rem' }}>
                    No monthly net sales for {selectedRep}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

ProdMgrsMonthlyNetSalesTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
  selectedYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default React.memo(ProdMgrsMonthlyNetSalesTable);
